import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useSearch } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { NavHeader } from '@/components/layout/nav-header';
import { VenueTimeFormats } from '@/utils/timezone';
import { ArrowLeft, Copy, RotateCcw, Check, Sparkles, Share2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ClipManager } from '@/components/admin/clip-manager';

interface SetDetailsData {
  booking: {
    id: number;
    timeSlot: {
      startTime: string;
      endTime: string;
    };
    dj: {
      displayName: string;
      instagramHandle: string;
      location: string;
    };
    causeLink?: string;
    notes?: string;
  };
  set: {
    id: number;
    genres: string[];
    setlist: string;
    causeLink: string;
    causeLabel: string;
    causeInstagramUrl: string;
    episodeNumber?: string;
    qrCodeLink?: string;
    publicVideoUrl?: string;
    keyMoments: Array<{
      timestamp: string;
      label: string;
    }>;
  };
}

export default function AdminSetDetailsPage() {
  const { bookingId } = useParams();
  const [, setLocation] = useLocation();
  const searchParams = useSearch();
  
  // Get the recordings tab from URL params
  const urlParams = new URLSearchParams(searchParams);
  const recordingsTab = urlParams.get('from') || 'all';
  const [data, setData] = useState<SetDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form fields
  const [episodeNumber, setEpisodeNumber] = useState('');
  const [qrCodeLink, setQrCodeLink] = useState('https://');
  const [youtubeLink, setYoutubeLink] = useState('https://');
  const [instagramCaption, setInstagramCaption] = useState('');
  const [youtubeDescription, setYoutubeDescription] = useState('');
  
  // Store original generated content for reset functionality
  const [originalInstagramCaption, setOriginalInstagramCaption] = useState('');
  const [originalYoutubeDescription, setOriginalYoutubeDescription] = useState('');
  
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isGeneratingCaptions, setIsGeneratingCaptions] = useState(false);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [enableVideoDownload, setEnableVideoDownload] = useState(false);
  const [enableAudioDownload, setEnableAudioDownload] = useState(false);
  const [enableTimestampDownloads, setEnableTimestampDownloads] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const savedPermissions = useRef({ video: false, audio: false, timestamps: false });

  useEffect(() => {
    if (bookingId) {
      fetchSetDetails();
    }
  }, [bookingId]);

  // Load existing share link for this booking
  useEffect(() => {
    if (!bookingId) return;
    const loadShareLink = async () => {
      try {
        const res = await fetch(`/api/admin/share-links?bookingId=${bookingId}`);
        if (res.ok) {
          const links = await res.json();
          if (links.length > 0) {
            const link = links[0];
            setShareToken(link.token);
            setEnableVideoDownload(link.enableVideoDownload);
            setEnableAudioDownload(link.enableAudioDownload);
            setEnableTimestampDownloads(link.enableTimestampDownloads);
            savedPermissions.current = {
              video: link.enableVideoDownload,
              audio: link.enableAudioDownload,
              timestamps: link.enableTimestampDownloads,
            };
          }
        }
      } catch {
        // Ignore
      }
    };
    loadShareLink();
  }, [bookingId]);

  // Share link mutation
  const shareLinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/share-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId: Number(bookingId),
          enableVideoDownload,
          enableAudioDownload,
          enableTimestampDownloads,
        }),
      });
      if (!res.ok) throw new Error("Failed to create share link");
      return res.json();
    },
    onSuccess: (linkData) => {
      setShareToken(linkData.token);
      savedPermissions.current = {
        video: enableVideoDownload,
        audio: enableAudioDownload,
        timestamps: enableTimestampDownloads,
      };
      const url = `${window.location.origin}/set/${linkData.token}`;
      navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    },
  });

  const shareUrl = shareToken ? `${window.location.origin}/set/${shareToken}` : null;
  const permissionsChanged = shareToken && (
    enableVideoDownload !== savedPermissions.current.video ||
    enableAudioDownload !== savedPermissions.current.audio ||
    enableTimestampDownloads !== savedPermissions.current.timestamps
  );

  const fetchSetDetails = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/admin/bookings/${bookingId}/set-details`);
      if (!response.ok) throw new Error('Failed to fetch set details');
      
      const data = await response.json();
      setData(data);
      
      // Populate form fields if they already exist (for posted items)
      if (data.set.episodeNumber) {
        setEpisodeNumber(data.set.episodeNumber);
      }
      if (data.set.qrCodeLink) {
        setQrCodeLink(data.set.qrCodeLink);
      }
      if (data.set.publicVideoUrl) {
        setYoutubeLink(data.set.publicVideoUrl);
      }
      
      // Initialize with empty captions - will be generated on demand
      setInstagramCaption('');
      setYoutubeDescription('');
      setOriginalInstagramCaption('');
      setOriginalYoutubeDescription('');
    } catch (error) {
      console.error('Error fetching set details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load set details',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateCaptions = (data: SetDetailsData) => {
    const { booking, set } = data;
    const artistName = booking.dj.displayName;
    const instagramHandle = booking.dj.instagramHandle ? `@${booking.dj.instagramHandle.replace('@', '')}` : '';
    const genres = set.genres.join(', ');
    
    // Format key moments for Instagram
    const keyMomentsText = set.keyMoments
      .filter(km => km.timestamp && km.label)
      .map((km, index) => `${km.timestamp} - ${km.label}`)
      .slice(0, 5)
      .join('\n');
    
    // Instagram caption template (fallback with exactly 30 hashtags)
    const genreHashtags = genres.toLowerCase().split(', ').map(g => `#${g.replace(/\s+/g, '')}`).slice(0, 5);
    const baseHashtags = ['#clubhousefm', '#losangeles', '#radiodjs', '#dj', '#boilerroom', '#charity', '#musicforchange', '#fundraiser', '#undergroundmusic', '#radioshow', '#donateanddance', '#nonprofitmusic', '#communityradio', '#giveback', '#djforacause', '#goodvibesonly', '#clubculture', '#livestreamdj', '#boilerroomstyle', '#losangelesmusic', '#newmusic', '#musicforgood', '#supportthecause', '#livemusic', '#djset'];
    const artistHashtag = `#${artistName.toLowerCase().replace(/\s+/g, '')}`;
    const causeHashtag = `#${set.causeLabel?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'charity'}`;
    
    // Combine and ensure exactly 30 hashtags
    const allHashtags = [...new Set([...genreHashtags, artistHashtag, causeHashtag, ...baseHashtags])].slice(0, 30);
    // If we still need more, add some filler hashtags
    while (allHashtags.length < 30) {
      const fillers = ['#radiolife', '#musicmatters', '#changemakers', '#socialimpact', '#musiccommunity'];
      const tag = fillers[allHashtags.length % fillers.length];
      if (!allHashtags.includes(tag)) allHashtags.push(tag);
    }
    
    const instagram = `${instagramHandle} comes by the Los Angeles Clubhouse to spin a radio set full of ${genres} to support ${set.causeInstagramUrl || '@' + set.causeLabel?.toLowerCase().replace(/[^a-z0-9]/g, '') || '@charity'}. Link in bio for full set & donation link.

${set.causeLabel || 'This cause'} is dedicated to making a positive impact in our community. Through their work, they're creating meaningful change and improving lives every day.

Help us raise awareness and funds for social impact using the power of music by subscribing to our YouTube channel.

If you want to do a radio set in the Clubhouse for a cause, DM us with your mix.

${allHashtags.join(' ')}`;

    // YouTube description template
    const youtube = `Sunflat spins a set full of Electronic, Indie, and Dance from the Los Angeles Clubhouse.

Their set is to help raise awareness and funds for Cure Alzheimer's Fund.

Cure Alzheimer's Fund (CureALZ) is a nonprofit organization dedicated to funding research with the highest probability of preventing, slowing, or reversing Alzheimer's disease. Established in 2004 by 3 founding families; Henry McCance, Jacqui and Jeff Morris, and Phyllis Rappaport, the organization was born out of frustration with the slow pace of Alzheimer's research. Applying principles from venture capital and corporate startups, they aimed to accelerate research and eradicate the disease.

To learn more and donate to the cause, click here:

https://curealz.org/

Spread the word, tag your friends, and let's make a difference together!

▽ Want to DJ in the Clubhouse for a cause? DM us and submit your mix.

☐ Follow us

Instagram: / @clubhousefm.la

Learn more about the Clubhouse FM project:

https://clubhousefm.com/

#radiodjs #dj #lotradio #bollerroom #losangeles #charity #curefest #npr #kcre #garage #giveabeat #housemusic #housemusic #radiodjs #radio`;

    return { instagram, youtube };
  };

  const generateCaptionsWithAI = async () => {
    if (!data) return;
    
    setIsGeneratingCaptions(true);
    try {
      const response = await fetch(`/api/admin/bookings/${bookingId}/generate-captions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking: data.booking,
          set: data.set,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate captions');
      }

      const generatedCaptions = await response.json();
      
      // Update the captions with AI-generated content
      setInstagramCaption(generatedCaptions.instagram);
      setYoutubeDescription(generatedCaptions.youtube);
      setOriginalInstagramCaption(generatedCaptions.instagram);
      setOriginalYoutubeDescription(generatedCaptions.youtube);
      
      toast({
        title: 'Success',
        description: 'Captions generated with AI successfully',
      });
    } catch (error) {
      console.error('Error generating captions with AI:', error);
      
      // Fall back to local generation if AI fails
      const fallbackCaptions = generateCaptions(data);
      setInstagramCaption(fallbackCaptions.instagram);
      setYoutubeDescription(fallbackCaptions.youtube);
      setOriginalInstagramCaption(fallbackCaptions.instagram);
      setOriginalYoutubeDescription(fallbackCaptions.youtube);
      
      toast({
        title: 'AI Generation Failed',
        description: 'Using fallback template instead. Check your OpenAI credits.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingCaptions(false);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast({
        title: 'Copied!',
        description: 'Text copied to clipboard',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy text',
        variant: 'destructive',
      });
    }
  };

  const handleReset = (field: 'instagram' | 'youtube') => {
    if (field === 'instagram') {
      setInstagramCaption(originalInstagramCaption);
    } else {
      setYoutubeDescription(originalYoutubeDescription);
    }
    toast({
      title: 'Reset',
      description: `${field === 'instagram' ? 'Instagram caption' : 'YouTube description'} reset to original`,
    });
  };

  const handleMarkAsPosted = async () => {
    if (!episodeNumber || !qrCodeLink || !youtubeLink) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch(`/api/admin/bookings/${bookingId}/mark-as-posted`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          episodeNumber,
          qrCodeLink,
          youtubeLink,
          instagramCaption,
          youtubeDescription,
        }),
      });

      if (!response.ok) throw new Error('Failed to mark as posted');

      toast({
        title: 'Success',
        description: 'Set marked as posted successfully',
      });

      // Navigate back to admin recordings page
      setLocation(`/admin/bookings?tab=recordings&recordingsTab=${recordingsTab}`);
    } catch (error) {
      console.error('Error marking as posted:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark as posted',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white">
        <NavHeader />
        <div className="flex items-center justify-center h-96">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white">
        <NavHeader />
        <div className="flex items-center justify-center h-96">
          <p>Set details not found</p>
        </div>
      </div>
    );
  }

  const { booking, set } = data;

  return (
    <div className="min-h-screen bg-clubhouse-black text-white">
      <NavHeader 
        breadcrumb={
          <div className="flex items-center gap-2 text-sm">
            <button 
              onClick={() => setLocation(`/admin/bookings?tab=recordings&recordingsTab=${recordingsTab}`)}
              className="text-gray-400 hover:text-white"
            >
              Recordings
            </button>
            <span className="text-gray-500">&gt;</span>
            <span>Set Details</span>
          </div>
        }
      />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back button and Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => setLocation(`/admin/bookings?tab=recordings&recordingsTab=${recordingsTab}`)}
              className="mb-4 -ml-2 text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Recordings
            </Button>
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold mb-2">{booking.dj.displayName} Clubhouse.FM set</h1>
              <Button
                variant="outline"
                onClick={() => setShowShareModal(true)}
                className="border-white/20 text-white/60 hover:bg-white/10 shrink-0"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
            <p className="text-gray-400">{VenueTimeFormats.dateOnly(booking.timeSlot.startTime)}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Set Details Card */}
              <Card className="bg-[#171717] border-white/10 p-6">
                <h2 className="text-lg font-semibold mb-4">Set Details</h2>
                <p className="text-sm text-gray-400 mb-4">Information submitted by the artist in set review</p>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Personal info</h3>
                    <div className="space-y-1 text-sm">
                      <p>Artist Name: <span className="text-white">{booking.dj.displayName}</span></p>
                      <p>Instagram Handle: <span className="text-white">@{booking.dj.instagramHandle?.replace('@', '')}</span></p>
                      <p>City of Origin: <span className="text-white">{booking.dj.location}</span></p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Key moments</h3>
                    <div className="space-y-2 text-sm">
                      {set.keyMoments.map((km, index) => (
                        <div key={index}>
                          <p className="text-gray-400 break-words">{km.timestamp} - {km.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Set Genres</h3>
                    <p className="text-sm text-white">{set.genres.join(', ')}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Setlist & Timestamps</h3>
                    <pre className="text-sm text-white whitespace-pre-wrap font-sans bg-[#1a1a1a] p-3 rounded-md border border-white/10">
                      {set.setlist}
                    </pre>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Philanthropic Cause</h3>
                    <div className="space-y-1 text-sm">
                      <p>Name of Cause: <span className="text-white">{set.causeLabel}</span></p>
                      <p>Cause's donation page URL: <a href={set.causeLink} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:underline break-all">{set.causeLink}</a></p>
                      {set.causeInstagramUrl && (
                        <p>Cause's Instagram URL: <a href={set.causeInstagramUrl} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:underline break-all">{set.causeInstagramUrl}</a></p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Episode Number */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Episode number</label>
                <p className="text-xs text-gray-400">Select the episode number</p>
                <Input
                  type="text"
                  value={episodeNumber}
                  onChange={(e) => setEpisodeNumber(e.target.value)}
                  placeholder="Enter episode number"
                  className="bg-[#1a1a1a] border-white/10"
                />
              </div>

              {/* QR Code Link */}
              <div className="space-y-2">
                <label className="text-sm font-medium">QR code link</label>
                <p className="text-xs text-gray-400">Add the donation QR code URL</p>
                <Input
                  type="url"
                  value={qrCodeLink}
                  onChange={(e) => setQrCodeLink(e.target.value)}
                  placeholder="https://"
                  className="bg-[#1a1a1a] border-white/10"
                />
              </div>

            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* AI Generation Button */}
              <div className="flex justify-end">
                <Button
                  onClick={generateCaptionsWithAI}
                  disabled={isGeneratingCaptions || !data}
                  variant="outline"
                  className="border-white text-white/60 hover:bg-white hover:text-clubhouse-black"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {isGeneratingCaptions ? 'Generating...' : 'Generate with AI'}
                </Button>
              </div>

              {/* Instagram Caption */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Instagram caption</label>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReset('instagram')}
                      disabled={!originalInstagramCaption}
                      className="text-gray-400 hover:text-white disabled:opacity-50"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Reset changes
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(instagramCaption, 'instagram')}
                      disabled={!instagramCaption}
                      className="text-gray-400 hover:text-white disabled:opacity-50"
                    >
                      {copiedField === 'instagram' ? (
                        <Check className="h-4 w-4 mr-1" />
                      ) : (
                        <Copy className="h-4 w-4 mr-1" />
                      )}
                      Copy text
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Review and make changes to the Instagram caption before copy/pasting</p>
                <Textarea
                  value={instagramCaption}
                  onChange={(e) => setInstagramCaption(e.target.value)}
                  rows={10}
                  placeholder="Click 'Generate with AI' to create an Instagram caption..."
                  className="bg-[#1a1a1a] border-white/10 text-sm font-mono"
                />
              </div>

              {/* YouTube Description */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Youtube description</label>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReset('youtube')}
                      disabled={!originalYoutubeDescription}
                      className="text-gray-400 hover:text-white disabled:opacity-50"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Reset changes
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(youtubeDescription, 'youtube')}
                      disabled={!youtubeDescription}
                      className="text-gray-400 hover:text-white disabled:opacity-50"
                    >
                      {copiedField === 'youtube' ? (
                        <Check className="h-4 w-4 mr-1" />
                      ) : (
                        <Copy className="h-4 w-4 mr-1" />
                      )}
                      Copy text
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Review and make changes to the YouTube video description before copy/pasting</p>
                <Textarea
                  value={youtubeDescription}
                  onChange={(e) => setYoutubeDescription(e.target.value)}
                  rows={15}
                  placeholder="Click 'Generate with AI' to create a YouTube description..."
                  className="bg-[#1a1a1a] border-white/10 text-sm font-mono"
                />
              </div>

              {/* YouTube Link */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Link to uploaded video</label>
                <p className="text-xs text-gray-400">Add the YouTube video URL</p>
                <Input
                  type="url"
                  value={youtubeLink}
                  onChange={(e) => setYoutubeLink(e.target.value)}
                  placeholder="https://"
                  className="bg-[#1a1a1a] border-white/10"
                />
              </div>

              {/* Mark as Posted Button */}
              <div className="pt-4">
                <Button
                  onClick={handleMarkAsPosted}
                  disabled={isSaving || !episodeNumber || !qrCodeLink || !youtubeLink}
                  className="w-full bg-white hover:bg-white/90 text-black font-semibold"
                >
                  {isSaving ? 'Saving...' : (data?.set?.publicVideoUrl ? 'Update details' : 'Mark as posted')}
                </Button>
              </div>
            </div>
          </div>

          {/* Clip Manager - full width */}
          {data?.set?.id && (
            <div className="mt-8">
              <ClipManager setId={data.set.id} />
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-white/10 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-lg font-semibold mb-2">Clubhouse.FM</h3>
            <p className="text-sm text-gray-400 mb-4">A Non-for-Profit Community Project Centered on Music & Culture</p>
            
            <h4 className="font-semibold mb-2">Powered by Afters Club & Friends</h4>
            <p className="text-sm text-gray-400 mb-2">
              Clubhouse.FM is a non-for-profit radio station based in Los Angeles supporting DJs and artists who want to raise funds and 
              awareness for causes they care about. We do this by providing free access to world-class streaming technology, high-end 
              production equipment, our influence in the LA music scene and a state-of-the-art creative space.
            </p>
            <p className="text-sm text-gray-400">
              Through our unique partnerships with the we are able to spread this positive sentiment around the world and truly make a 
              difference.
            </p>
            
            <p className="text-sm text-gray-400 mt-4">
              Every DJ set is for a cause. Choose one that you are passionate about. We'll open up our space at no cost to you and amplify 
              your voice on our platform.
            </p>
          </div>
        </div>
      </footer>

      {/* Share modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Share this set</DialogTitle>
            <DialogDescription>
              Send this link to the artist so they can review the recording, add timestamps, or download their set.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {shareUrl && (
              <div
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
                className="flex items-center gap-2 bg-[#1a1a1a] border border-white/10 rounded-md px-3 py-2.5 cursor-pointer hover:border-white/20 transition-colors overflow-hidden"
              >
                <span className="text-[13px] text-white/60 break-all flex-1">{shareUrl}</span>
                {linkCopied ? (
                  <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                ) : (
                  <Copy className="h-4 w-4 text-white/40 shrink-0" />
                )}
              </div>
            )}

            {linkCopied && (
              <p className="text-[13px] text-emerald-400 text-center">Copied to clipboard</p>
            )}

            <p className="text-[13px] text-white/30">
              Anyone with this link will be able to view the recording.
            </p>

            <div className="space-y-3 pt-1">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableVideoDownload}
                  onCheckedChange={(checked) => setEnableVideoDownload(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Video Download</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableAudioDownload}
                  onCheckedChange={(checked) => setEnableAudioDownload(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Audio Download</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={enableTimestampDownloads}
                  onCheckedChange={(checked) => setEnableTimestampDownloads(checked === true)}
                  className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:text-black"
                />
                <span className="text-[14px] text-white/60">Enable Timestamp Downloads</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            {!shareUrl ? (
              <Button
                onClick={() => shareLinkMutation.mutate()}
                disabled={shareLinkMutation.isPending}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                {shareLinkMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
                ) : (
                  "Create & Copy Link"
                )}
              </Button>
            ) : permissionsChanged ? (
              <Button
                onClick={() => shareLinkMutation.mutate()}
                disabled={shareLinkMutation.isPending}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                {shareLinkMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Updating...</>
                ) : (
                  "Update & Copy Link"
                )}
              </Button>
            ) : (
              <Button
                onClick={() => setShowShareModal(false)}
                className="bg-white hover:bg-white/90 text-black font-medium px-6"
              >
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}